import { Station } from './types';

type MapBoundingBox = {
  east: number;
  north: number;
  south: number;
  west: number;
};

const isCoordinate = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const isUsableBoundingBox = (bbox?: MapBoundingBox): bbox is MapBoundingBox =>
  !!bbox &&
  isCoordinate(bbox.north) &&
  isCoordinate(bbox.south) &&
  isCoordinate(bbox.east) &&
  isCoordinate(bbox.west);

/**
 * The bounding box and the station coordinates originate in Contentful, whose
 * `geonameInfo` is untyped — the corners have reached the browser as strings.
 * Google Maps throws an `InvalidValueError` on those, which takes the whole
 * page down, so skip whatever is unusable: an unusable box falls back to the
 * station coordinates, and the caller handles bounds that stay empty.
 */
export const extendBounds = (
  bounds: google.maps.LatLngBounds,
  { bbox, stations }: { bbox?: MapBoundingBox; stations?: Station[] },
) => {
  if (isUsableBoundingBox(bbox)) {
    bounds.extend({ lat: bbox.north, lng: bbox.west });
    bounds.extend({ lat: bbox.south, lng: bbox.west });
    bounds.extend({ lat: bbox.north, lng: bbox.east });
    bounds.extend({ lat: bbox.south, lng: bbox.east });

    return bounds;
  }

  stations?.forEach(station => {
    if (isCoordinate(station.latitude) && isCoordinate(station.longitude)) {
      bounds.extend({ lat: station.latitude, lng: station.longitude });
    }
  });

  return bounds;
};

export type { MapBoundingBox };
